import React, { useState } from 'react';
import { 
  Download, 
  Filter, 
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingCart,
  FileText
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const monthlyData = [
  { month: 'Jan', revenue: 45000, expenses: 32000, profit: 13000, orders: 156 },
  { month: 'Feb', revenue: 52000, expenses: 35000, profit: 17000, orders: 178 },
  { month: 'Mar', revenue: 48000, expenses: 33000, profit: 15000, orders: 165 },
  { month: 'Apr', revenue: 61000, expenses: 38000, profit: 23000, orders: 203 },
  { month: 'May', revenue: 70000, expenses: 42000, profit: 28000, orders: 234 },
  { month: 'Jun', revenue: 68000, expenses: 40000, profit: 28000, orders: 221 }
];

const categoryData = [
  { name: 'Software', value: 35000, percentage: 35 },
  { name: 'Hardware', value: 28000, percentage: 28 },
  { name: 'Services', value: 22000, percentage: 22 },
  { name: 'Consulting', value: 15000, percentage: 15 }
];

const topCustomers = [
  { name: 'Acme Corp', revenue: '$45,200', orders: 34, growth: '+15%' },
  { name: 'Tech Solutions', revenue: '$38,900', orders: 28, growth: '+22%' },
  { name: 'Global Inc', revenue: '$32,500', orders: 24, growth: '+8%' },
  { name: 'Digital Pro', revenue: '$28,700', orders: 21, growth: '+12%' },
  { name: 'Innovation Labs', revenue: '$24,300', orders: 18, growth: '-5%' }
];

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

// export function ReportsScreen() {
    
const Reports = () => {
  const [dateRange, setDateRange] = useState('last-6-months');
  const [reportType, setReportType] = useState('overview');

  // Shared Theme Palette Constants (replacing your global CSS variables)
  const theme = {
    background: '#ffffff',
    foreground: '#0f172a',
    mutedForeground: '#64748b',
    border: '#e2e8f0',
    card: '#ffffff',
    mutedBg: '#f8fafc',
    primary: '#0f172a',
    primaryForeground: '#ffffff'
  };

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px', fontFamily: 'sans-serif', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      
      {/* Header Row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ color: theme.foreground, margin: '0 0 8px 0', fontSize: '28px', fontWeight: 'bold' }}>Reports & Analytics</h1>
          <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '15px' }}>Comprehensive business insights and performance metrics</p>
        </div>
        <button style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          padding: '10px 16px', 
          backgroundColor: theme.primary, 
          color: theme.primaryForeground, 
          borderRadius: '8px', 
          border: 'none', 
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          <Download style={{ width: '18px', height: '18px' }} />
          <span>Export Report</span>
        </button>
      </div>

      {/* Filters Section */}
      <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar style={{ width: '20px', height: '20px', color: theme.mutedForeground }} />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              style={{ padding: '8px 12px', backgroundColor: theme.mutedBg, color: theme.foreground, borderRadius: '8px', border: `1px solid ${theme.border}`, outline: 'none', cursor: 'pointer' }}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last-7-days">Last 7 Days</option>
              <option value="last-30-days">Last 30 Days</option>
              <option value="last-6-months">Last 6 Months</option>
              <option value="this-year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter style={{ width: '20px', height: '20px', color: theme.mutedForeground }} />
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              style={{ padding: '8px 12px', backgroundColor: theme.mutedBg, color: theme.foreground, borderRadius: '8px', border: `1px solid ${theme.border}`, outline: 'none', cursor: 'pointer' }}
            >
              <option value="overview">Overview</option>
              <option value="sales">Sales Report</option>
              <option value="customers">Customer Report</option>
              <option value="products">Product Report</option>
              <option value="financial">Financial Report</option>
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px' }}>
        
        {/* Metric Card 1 */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', marginBottom: '12px', justifyContent: 'space-between' }}>
            <div style={{ padding: '8px', backgroundColor: '#f0fdf4', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
              <DollarSign style={{ width: '24px', height: '24px', color: '#16a34a' }} />
            </div>
            <TrendingUp style={{ width: '20px', height: '20px', color: '#16a34a' }} />
          </div>
          <p style={{ color: theme.mutedForeground, fontSize: '14px', margin: '0 0 4px 0' }}>Total Revenue</p>
          <h2 style={{ color: theme.foreground, fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>$344,000</h2>
          <p style={{ color: '#16a34a', fontSize: '14px', margin: 0, fontWeight: '500' }}>+18.2% from last period</p>
        </div>

        {/* Metric Card 2 */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', marginBottom: '12px', justifyContent: 'space-between' }}>
            <div style={{ padding: '8px', backgroundColor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
              <ShoppingCart style={{ width: '24px', height: '24px', color: '#2563eb' }} />
            </div>
            <TrendingUp style={{ width: '20px', height: '20px', color: '#16a34a' }} />
          </div>
          <p style={{ color: theme.mutedForeground, fontSize: '14px', margin: '0 0 4px 0' }}>Total Orders</p>
          <h2 style={{ color: theme.foreground, fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>1,157</h2>
          <p style={{ color: '#16a34a', fontSize: '14px', margin: 0, fontWeight: '500' }}>+12.5% from last period</p>
        </div>

        {/* Metric Card 3 */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', marginBottom: '12px', justifyContent: 'space-between' }}>
            <div style={{ padding: '8px', backgroundColor: '#faf5ff', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
              <Users style={{ width: '24px', height: '24px', color: '#9333ea' }} />
            </div>
            <TrendingUp style={{ width: '20px', height: '20px', color: '#16a34a' }} />
          </div>
          <p style={{ color: theme.mutedForeground, fontSize: '14px', margin: '0 0 4px 0' }}>New Customers</p>
          <h2 style={{ color: theme.foreground, fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>234</h2>
          <p style={{ color: '#16a34a', fontSize: '14px', margin: 0, fontWeight: '500' }}>+23.1% from last period</p>
        </div>

        {/* Metric Card 4 */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'between', marginBottom: '12px', justifyContent: 'space-between' }}>
            <div style={{ padding: '8px', backgroundColor: '#fff7ed', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
              <FileText style={{ width: '24px', height: '24px', color: '#ea580c' }} />
            </div>
            <TrendingDown style={{ width: '20px', height: '20px', color: '#dc2626' }} />
          </div>
          <p style={{ color: theme.mutedForeground, fontSize: '14px', margin: '0 0 4px 0' }}>Avg Order Value</p>
          <h2 style={{ color: theme.foreground, fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>$297</h2>
          <p style={{ color: '#dc2626', fontSize: '14px', margin: 0, fontWeight: '500' }}>-3.2% from last period</p>
        </div>
      </div>

      {/* Charts Row 1 (Flex Wrapping Layout) */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        
        {/* Revenue & Expenses */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px', flex: '1 1 450px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: theme.foreground, margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>Revenue vs Expenses</h3>
            <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '14px' }}>Monthly financial performance</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#f59e0b" name="Expenses" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Profit Trend */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px', flex: '1 1 450px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: theme.foreground, margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>Profit Trend</h3>
            <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '14px' }}>Monthly profit analysis</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="reportsColorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop key="stop1" offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop key="stop2" offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="profit"
                stroke="#10b981"
                fillOpacity={1}
                fill="url(#reportsColorProfit)"
                name="Profit"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        
        {/* Category Distribution */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px', flex: '1 1 300px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: theme.foreground, margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>Sales by Category</h3>
            <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '14px' }}>Revenue distribution</p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {categoryData.map((item, index) => (
              <div key={item.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: COLORS[index] }}></div>
                  <span style={{ fontSize: '14px', color: theme.foreground }}>{item.name}</span>
                </div>
                <span style={{ fontSize: '14px', color: theme.mutedForeground }}>{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Customers */}
        <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px', flex: '2 1 500px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ color: theme.foreground, margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>Top Customers</h3>
            <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '14px' }}>Highest revenue contributors</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {topCustomers.map((customer, index) => (
              <div key={customer.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: '#f1f5f9', borderRadius: '50%' }}>
                    <span style={{ color: theme.primary, fontWeight: '600', fontSize: '14px' }}>#{index + 1}</span>
                  </div>
                  <div>
                    <p style={{ color: theme.foreground, margin: '0 0 2px 0', fontWeight: '500' }}>{customer.name}</p>
                    <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '13px' }}>{customer.orders} orders</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ color: theme.foreground, margin: '0 0 2px 0', fontWeight: '600' }}>{customer.revenue}</p>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '500', color: customer.growth.startsWith('+') ? '#16a34a' : '#dc2626' }}>
                    {customer.growth}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Orders Timeline */}
      <div style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, borderRadius: '12px', padding: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: theme.foreground, margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600' }}>Order Volume Timeline</h3>
          <p style={{ color: theme.mutedForeground, margin: 0, fontSize: '14px' }}>Daily order trends</p>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="orders" 
              stroke="#3b82f6" 
              strokeWidth={3}
              dot={{ fill: '#3b82f6', r: 4 }}
              name="Orders"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

    </div>
  );
}

export default Reports;